import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { createInteriorRenderer } from "../../lib/interiorRender";
import { periksa, pusatkan } from "../../lib/interiorRules";
import { defPerabotMall, pastikanMall } from "../../lib/mallRegistry";
import { useMapGestures } from "../../lib/useMapGestures";
import { FONT } from "../kota/ui";
import { DalamTopBar, RuangTabs, DalamDock, TataBar, PopoverPerabot, HintPill, TidurPill } from "./DalamUI";
import { PerabotSheet, DalamBantuanSheet } from "./PerabotSheet";

const TOP_PX = 124;                      // bar atas + tab ruang
const BOTTOM = { dock: 100, tata: 100, sheet: 0.6 };
const idMall = key => (key.startsWith("m_") ? key.slice(2) : null);

// Ruang dalam rumah: jalan-jalan (ketuk lantai), tab ruang memfokuskan kamera, mode Tata (pemilik) dengan draft + undo + Simpan.
// Tamu melihat & berjalan; pemilik berdiri di ruang tamu saat ada tamu. Ketuk kasur (pemilik, bukan mode tata) → istirahat.
// Perabot Mall (key m_<id>) dimuat dari registry Mall; "Coba di rumah" (dari halaman /mall → ?coba=<id>): barang Mall di tangan
// tanpa dimiliki → Beli & Simpan. Tombol Mall di sini membuka halaman /mall (kembali ke rumah lewat ?dari=rumah).
// tidurSampai (ms epoch) dari halaman induk: karakter jalan ke kasur → berbaring (zzz) sampai waktunya / ditekan "Bangun".
export function DalamRumah({ token, data, katalog, pemilik, saldo, avatar, cobaAwal, tidurSampai, onBangun, onUpdate, onSaldo, onKeluar, onIstirahat }) {
  const nav = useNavigate();
  const keMall = () => nav(`/mall?kota=${data.pemilik.city_id || "kediri"}&dari=rumah`);
  const katDasar = katalog.interior;
  const canvasRef = useRef(null), rendRef = useRef(null), refs = useRef({});
  const [mallDefs, setMallDefs] = useState([]);
  const [ruang, setRuang] = useState("semua"), [tata, setTata] = useState(false), [draft, setDraft] = useState(null), [undo, setUndo] = useState([]);
  const [pilih, setPilih] = useState(null), [taruh, setTaruh] = useState(null), [pindah, setPindah] = useState(null), [coba, setCoba] = useState(null);
  const [sheet, setSheet] = useState(null), [busy, setBusy] = useState(false), [sisaTidur, setSisaTidur] = useState(0);
  const interior = tata && draft ? draft : data.rumah.interior;
  const kat = useMemo(() => ({ ...katDasar, perabot: [...katDasar.perabot, ...mallDefs] }), [katDasar, mallDefs]);
  const defMap = useMemo(() => Object.fromEntries(kat.perabot.map(p => [p.key, p])), [kat]);
  const dimiliki = data.rumah.perabot_dimiliki || {};
  const dimilikiEfektif = coba ? { ...dimiliki, [coba.key]: (dimiliki[coba.key] || 0) + 1 } : dimiliki;   // barang yang dicoba dihitung seolah punya 1
  refs.current = { tata, draft, pilih, taruh, pindah, pemilik, interior, avatar, kat, defMap, dimiliki: dimilikiEfektif, coba };

  // Definisi perabot Mall yang dipakai di interior / dimiliki / sedang dicoba → registry + renderer
  const muatMall = useCallback(async (extra = []) => {
    const ids = [...new Set([...data.rumah.interior.perabot.map(p => p.key), ...Object.keys(dimiliki), ...extra].map(idMall).filter(Boolean))];
    if (!ids.length) return [];
    const defs = (await pastikanMall(ids)).map(defPerabotMall);
    setMallDefs(d => { const m = new Map(d.map(x => [x.key, x])); defs.forEach(x => m.set(x.key, x)); return [...m.values()]; });
    rendRef.current?.setMall(defs);
    return defs;
  }, [data.rumah.interior, data.rumah.perabot_dimiliki]); // eslint-disable-line
  useEffect(() => { muatMall().catch(() => {}); }, [muatMall]);

  // renderer + loop
  useEffect(() => {
    const r = createInteriorRenderer(canvasRef.current, katDasar); rendRef.current = r;
    r.fokus(null, TOP_PX, BOTTOM.dock, true);
    r.setPemain(avatar, katDasar.pintu_depan.x + 0.5, katDasar.grid.d - 0.5);
    let raf; const loop = () => { r.render(performance.now()); raf = requestAnimationFrame(loop); }; raf = requestAnimationFrame(loop);
    window.__dalam = { posSel: (x, y) => r.posSel(x, y), posPerabot: id => r.posPerabot(id), pemain: () => r.posisiPemain(), kamera: () => ({ ...r.cam }), hit: (sx, sy) => r.hit(sx, sy), duduk: () => r.sedangDuduk() };
    return () => { cancelAnimationFrame(raf); r.destroy(); delete window.__dalam; };
  }, []); // eslint-disable-line
  useEffect(() => {
    const r = rendRef.current; if (!r) return;
    r.setData({ interior, lain: [] });
    if (pemilik) return;
    // pemilik berdiri di sel kosong terdekat pusat ruang tamu saat ada tamu
    const T = katDasar.ruang.tamu, mx = T.x + Math.floor(T.w / 2), my = 2;
    const kandidat = [[0, 0], [-1, 0], [1, 0], [0, 1], [-1, 1], [1, 1], [-2, 0], [2, 0], [-1, 2], [1, 2]].map(([a, b]) => [mx + a, my + b]), [ox, oy] = kandidat.find(([x, y]) => r.bisaLewat(x, y)) || [mx, my];
    r.setData({ interior, lain: [{ avatar: data.pemilik.avatar, x: ox + 0.5, y: oy + 0.5, facing: -1 }] });
  }, [interior, pemilik, data.pemilik]);
  useEffect(() => { rendRef.current?.setEdit(tata ? { pilih } : null); }, [tata, pilih]);
  useEffect(() => { window.__dalamDraft = draft; }, [draft]);   // kait baca-saja untuk pengujian otomatis
  // Kamera ikut tab ruang (dianimasikan) dan panel bawah; saat sheet dibuka/ditutup kamera melompat langsung agar ketukan
  // berikutnya (yang ditunda 260 ms oleh deteksi ketuk ganda) tidak mengenai sel yang salah.
  const sheetRef = useRef(sheet);
  useEffect(() => { const H = window.innerHeight, lompat = sheetRef.current !== sheet; sheetRef.current = sheet; rendRef.current?.fokus(ruang === "semua" ? null : ruang, TOP_PX, sheet === "perabot" || sheet === "gaya" ? Math.round(H * BOTTOM.sheet) : tata ? BOTTOM.tata : BOTTOM.dock, lompat); }, [ruang, sheet, tata]);

  // ---------- mode tata ----------
  const mulaiTata = () => { if (tidurSampai) onBangun?.(); rendRef.current?.setDuduk(null); setDraft(JSON.parse(JSON.stringify(data.rumah.interior))); setUndo([]); setPilih(null); setTata(true); };

  // ---------- tidur: jalan ke sel di samping kasur → berbaring selama TIDUR_DETIK (hitung mundur) → bangun berdiri di samping kasur ----------
  useEffect(() => {
    const r = rendRef.current;
    if (!tidurSampai || !r) { r?.setTidur(null); setSisaTidur(0); return; }
    const kasur = data.rumah.interior.perabot.find(q => q.key === "kasur") || data.rumah.interior.perabot.find(q => /kasur|ranjang|tempat tidur/i.test(defMap[q.key]?.nama || ""));
    if (!kasur) { onBangun?.(); return; }
    const samping = r.selSamping(kasur.id);
    setRuang("kamar");   // kamera ke kamar
    const berbaring = () => r.setTidur({ id: kasur.id, mulai: Date.now() });
    if (samping && r.jalanKe(samping[0], samping[1], berbaring)) { /* berbaring setelah sampai */ } else berbaring();
    const tick = () => { const sisa = Math.max(0, Math.ceil((tidurSampai - Date.now()) / 1000)); setSisaTidur(sisa); if (sisa <= 0) onBangun?.(); };
    tick(); const iv = setInterval(tick, 500);
    return () => { clearInterval(iv); r.setTidur(null); };
  }, [tidurSampai]); // eslint-disable-line
  const batalTata = () => { setTata(false); setDraft(null); setPilih(null); setTaruh(null); setPindah(null); setSheet(null); setCoba(null); };
  const ubahDraft = fn => setDraft(d => { setUndo(u => [...u.slice(-29), d]); return fn(d); });
  const urungkan = () => setUndo(u => { if (!u.length) return u; setDraft(u[u.length - 1]); setPilih(null); return u.slice(0, -1); });
  const cek = (item, lain) => { const salah = periksa(refs.current.kat, item, lain, refs.current.dimiliki); if (salah) toast.error(salah); return !salah; };
  const putar = () => { const d = refs.current.draft, p = d.perabot.find(q => q.id === refs.current.pilih); if (!p) return; const baru = { ...p, rot: (p.rot + 1) % 4 }; if (cek(baru, d.perabot)) ubahDraft(x => ({ ...x, perabot: x.perabot.map(q => (q.id === p.id ? baru : q)) })); };
  const singkirkan = () => { const id = refs.current.pilih; ubahDraft(x => ({ ...x, perabot: x.perabot.filter(q => q.id !== id) })); setPilih(null); };
  const taruhDi = (cx, cy) => {
    const { taruh: t, draft: d, kat: k, defMap: dm } = refs.current, def = dm[t.key]; if (!def) return toast.error("Barang belum siap, coba lagi");
    const item = { id: `n${Date.now().toString(36)}`, key: t.key, rot: t.rot, ...pusatkan(k, def, t.rot, cx, cy) };
    if (!cek(item, d.perabot)) return;
    ubahDraft(x => ({ ...x, perabot: [...x.perabot, item] })); setTaruh(null); setPilih(item.id);
  };
  const pindahKe = (cx, cy) => {
    const { pindah: id, draft: d, kat: k, defMap: dm } = refs.current, p = d.perabot.find(q => q.id === id); if (!p) return setPindah(null);
    const item = { ...p, ...pusatkan(k, dm[p.key], p.rot, cx, cy) };
    if (!cek(item, d.perabot)) return;
    ubahDraft(x => ({ ...x, perabot: x.perabot.map(q => (q.id === id ? item : q)) })); setPindah(null); setPilih(id);
  };
  const simpan = async () => {
    setBusy(true);
    try { const r = await api.rumahInterior(token, { perabot: draft.perabot.map(({ id, key, x, y, rot }) => ({ id, key, x, y, rot })), gaya: draft.gaya }); onUpdate(r); toast.success("Penataan rumah disimpan"); batalTata(); }
    catch (e) { toast.error(e.message); }
    setBusy(false);
  };
  const ubahGaya = (rk, patch) => ubahDraft(x => ({ ...x, gaya: { ...x.gaya, [rk]: { ...x.gaya[rk], ...patch } } }));
  const berubah = tata && draft && JSON.stringify(draft) !== JSON.stringify(data.rumah.interior);

  // ---------- Coba di rumah (barang Mall belum dimiliki) ----------
  const cobaTerpasang = !!(coba && draft && draft.perabot.filter(q => q.key === coba.key).length > (dimiliki[coba.key] || 0));
  const mulaiCoba = async it => {
    try {
      const defs = await muatMall([`m_${it.id}`]);
      if (!defs.find(d => d.key === `m_${it.id}`)) return toast.error("Barang tidak tersedia");
    } catch (e) { return toast.error(e.message); }
    if (!refs.current.tata) mulaiTata();
    setCoba({ id: it.id, key: `m_${it.id}`, nama: it.nama, harga: it.harga });
    setSheet(null); setPilih(null); setPindah(null); setTaruh({ key: `m_${it.id}`, rot: 0 });
  };
  useEffect(() => { if (cobaAwal && pemilik) api.mallKode(cobaAwal).then(d => d.items[0] && mulaiCoba(d.items[0])).catch(() => {}); }, []); // eslint-disable-line
  const beliDanSimpan = async () => {
    if (saldo != null && saldo < coba.harga) return toast.error(`Koin kurang — butuh ${coba.harga}, saldo ${saldo}`);
    setBusy(true);
    try { const r = await api.mallBeli(token, coba.id); onSaldo?.(r.saldo); toast.success(`${coba.nama} dibeli · sisa ${r.saldo} koin`); }
    catch (e) { toast.error(e.message); setBusy(false); return; }
    await simpan();
  };

  // ---------- ketukan kanvas ----------
  const onTap = useCallback((sx, sy) => {
    const r = rendRef.current, h = r.hit(sx, sy), c = refs.current;
    if (c.tata) {
      if (c.taruh) { if (h.jenis === "lantai") taruhDi(h.x, h.y); return; }
      if (c.pindah) { if (h.jenis === "lantai") pindahKe(h.x, h.y); else if (h.jenis === "perabot") setPindah(null); return; }
      setPilih(h.jenis === "perabot" ? h.id : null); return;
    }
    if (r.sedangTidur()) return;   // sedang tidur: abaikan ketukan kanvas (bangun lewat tombol)
    if (h.jenis === "perabot" && h.key === "kasur" && c.pemilik) return onIstirahat();
    if (h.jenis === "perabot" && c.avatar && r.adaDudukan(h.key)) {   // sofa/kursi: jalan ke sampingnya lalu duduk (animasi santai)
      const s = r.selSamping(h.id); r.setDuduk(null);
      if (!s || !r.jalanKe(s[0], s[1], () => r.setDuduk({ id: h.id, mulai: performance.now() }))) toast("Tidak bisa ke sana");
      return;
    }
    if (h.jenis === "lantai" && c.avatar) { r.setDuduk(null); if (!r.jalanKe(h.x, h.y)) toast("Tidak bisa berjalan ke sana"); }
  }, []); // eslint-disable-line
  const gestures = useMapGestures(rendRef, onTap, { ketukDua: false });   // ketuk langsung jalan (tanpa jeda deteksi ketuk ganda)

  // ---------- seret perabot langsung di kanvas (mode tata): tekan perabot → geser → lepas; bayangan hijau/merah ----------
  const dragRef = useRef(null);
  const lokal = (e, t) => { const r = e.currentTarget.getBoundingClientRect(); return [t.clientX - r.left, t.clientY - r.top]; };
  const seretMulai = (e, t) => {
    const c = refs.current, r = rendRef.current;
    if (!c.tata || c.taruh || !r) return false;
    const [sx, sy] = lokal(e, t), h = r.hit(sx, sy);
    if (h.jenis !== "perabot") return false;
    const p = c.draft.perabot.find(q => q.id === h.id), [cx0, cy0] = r.sel(sx, sy);
    // offset genggam: sel asal perabot relatif sel di bawah jari → perabot mengikuti jari, tidak melompat ke tengah
    dragRef.current = { id: h.id, sx, sy, moved: false, item: null, valid: false, off: p ? [p.x - cx0, p.y - cy0] : [0, 0] };
    setPilih(h.id); setPindah(null);
    return true;
  };
  const seretGeser = (e, t) => {
    const g = dragRef.current; if (!g) return false;
    const c = refs.current, r = rendRef.current, [sx, sy] = lokal(e, t);
    if (!g.moved && Math.hypot(sx - g.sx, sy - g.sy) < 6) return true;
    g.moved = true;
    const p = c.draft.perabot.find(q => q.id === g.id); if (!p) return true;
    const [cx, cy] = r.sel(sx, sy), item = { ...p, x: cx + g.off[0], y: cy + g.off[1] };
    g.item = item; g.valid = !periksa(c.kat, item, c.draft.perabot, c.dimiliki);
    r.setEdit({ pilih: g.id, seret: g.id, hantu: { key: p.key, rot: p.rot, x: item.x, y: item.y, valid: g.valid } });
    return true;
  };
  const seretSelesai = () => {
    const g = dragRef.current; if (!g) return false;
    dragRef.current = null;
    window.__seretTerakhir = { moved: g.moved, valid: g.valid, item: g.item };   // kait uji
    if (g.moved && g.item && g.valid) ubahDraft(x => ({ ...x, perabot: x.perabot.map(q => (q.id === g.id ? g.item : q)) }));
    else if (g.moved && g.item) toast.error("Tidak muat di situ");
    rendRef.current?.setEdit({ pilih: g.id });
    return g.moved;   // ketukan tanpa geser diteruskan sebagai ketuk biasa (pilih perabot)
  };
  const kanvasHandlers = {
    onTouchStart: e => { if (e.touches.length === 1 && seretMulai(e, e.touches[0])) return; gestures.onTouchStart(e); },
    onTouchMove: e => { if (dragRef.current) { seretGeser(e, e.touches[0]); return; } gestures.onTouchMove(e); },
    onTouchEnd: e => { if (dragRef.current) { if (seretSelesai()) return; } gestures.onTouchEnd(e); },
    onMouseDown: e => { if (seretMulai(e, e)) return; gestures.onMouseDown(e); },
    onMouseMove: e => { if (dragRef.current) { seretGeser(e, e); return; } gestures.onMouseMove(e); },
    onMouseUp: e => { if (dragRef.current) { if (seretSelesai()) return; } gestures.onMouseUp(e); },
    onMouseLeave: e => { if (dragRef.current) { seretSelesai(); return; } gestures.onMouseLeave?.(e); },
    onWheel: gestures.onWheel,
  };

  const pilihan = tata && pilih ? draft.perabot.find(q => q.id === pilih) : null;
  const hint = taruh ? `Ketuk lantai untuk ${coba?.key === taruh.key ? "mencoba" : "menaruh"} ${defMap[taruh.key]?.nama || "barang"}` : pindah ? "Ketuk lantai tujuan untuk memindahkan" : cobaTerpasang && !sheet ? `Mencoba ${coba.nama} · ${coba.harga} koin — geser/putar dulu, lalu Beli & Simpan` : null;

  return (
    <div data-testid="dalam-rumah" style={{ position: "absolute", inset: 0, zIndex: 7, fontFamily: FONT, background: "#efe6d6" }}>
      <canvas ref={canvasRef} data-testid="dalam-canvas" {...kanvasHandlers} style={{ display: "block", width: "100%", height: "100%", touchAction: "none", cursor: "pointer" }} />
      <DalamTopBar pemilik={data.pemilik} saya={pemilik} saldo={saldo} tata={tata} onKeluar={tata ? batalTata : onKeluar} onBantuan={() => setSheet("bantuan")} />
      <RuangTabs ruang={kat.ruang} aktif={ruang} onPilih={setRuang} />
      {!tata && <DalamDock saya={pemilik} onKeluar={onKeluar} onTata={mulaiTata} onMall={pemilik ? keMall : null} />}
      {tata && !sheet && <TataBar slot={draft.perabot.length} slotMaks={kat.slot_perabot_maks} bisaUndo={undo.length > 0} busy={busy} berubah={berubah} beli={cobaTerpasang ? coba : null} onUndo={urungkan} onPerabot={() => { setPilih(null); setSheet("perabot"); }} onGaya={() => { setPilih(null); setSheet("gaya"); }} onSimpan={cobaTerpasang ? beliDanSimpan : simpan} />}
      {hint && <HintPill teks={hint} onBatal={taruh || pindah ? () => { setTaruh(null); setPindah(null); } : null} />}
      {tidurSampai && !tata && <TidurPill sisa={sisaTidur} onBangun={onBangun} />}
      {pilihan && !taruh && !pindah && !sheet && <PopoverPerabot nama={defMap[pilihan.key]?.nama} posisi={() => rendRef.current?.posPerabot(pilihan.id)} onPutar={putar} onPindah={() => setPindah(pilihan.id)} onSingkirkan={singkirkan} />}
      {(sheet === "perabot" || sheet === "gaya") && draft && <PerabotSheet kat={kat} draft={draft} dimiliki={dimilikiEfektif} tabAwal={sheet === "gaya" ? "gaya" : "dasar"} ruangAwal={ruang === "semua" ? "tamu" : ruang} onTaruh={key => { setSheet(null); setTaruh({ key, rot: 0 }); }} onGaya={ubahGaya} onMall={keMall} onClose={() => setSheet(null)} />}
      {sheet === "bantuan" && <DalamBantuanSheet saya={pemilik} kat={kat} onClose={() => setSheet(null)} />}
    </div>
  );
}
